import ax from "axios";
import { scroller, animateScroll } from "react-scroll";
import { baseApi, logoBoxSizes } from "./constants";
import type { AdminConfig, AppContext, Size, Store } from "../types";
// .
const USDollar = new Intl.NumberFormat(`en-US`, {
    style: `currency`,
    currency: `USD`,
});

export const scrollerTo = (loc: string, ScrollOffset = 0): void => {
    scroller.scrollTo(loc, {
        duration: 400,
        delay: 0,
        smooth: `easeInOutQuart`,
        offset: ScrollOffset,
    });
};

export const navigateToTop = (navigate: (l: string) => void, l: string): void => {
    navigate(l);
    animateScroll.scrollTo(0, {
        duration: 400,
        delay: 0,
        smooth: `easeInOutQuart`,
        offset: 0,
    });
};

export const clearLocalStorage = (): void => {
    const saves: { n: string; d: string | null }[] = [];
    saves.push({ n: `config`, d: localStorage.getItem(`config`) });
    saves.push({ n: `adminConfig`, d: localStorage.getItem(`adminConfig`) });
    saves.push({ n: `programGuid`, d: localStorage.getItem(`programGuid`) });
    localStorage.clear();
    saves.forEach(d => {
        if (d.d) {
            localStorage.setItem(d.n, d.d);
        }
    });
};

export const enterInput = (e: React.KeyboardEvent, onEnter: () => void): void => {
    if (e.key === `Enter`) {
        e.preventDefault();
        onEnter();
    }
};

export const escInput = (e: React.KeyboardEvent, onEsc: () => void): void => {
    if (e.key === `Esc`) {
        e.preventDefault();
        onEsc();
    }
};

export const topSeparatorMargin = (size: Size): number => (size === `small` ? 1.25 : 3);

export const buttonGradient = (theme: {
    palette: { account: { accent?: string; accentGradientEnd?: string } };
}): string =>
    `linear-gradient(to bottom,${theme.palette.account.accent},${theme.palette.account.accentGradientEnd || `#999`})`;

export const svgGradient = (theme: {
    palette: { account: { accent?: string; accentGradientEnd?: string } };
}): React.JSX.Element => (
    <svg style={{ height: 0, width: 0 }}>
        <linearGradient id="multiStopGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={theme.palette.account.accent} />
            <stop offset="100%" stopColor={theme.palette.account.accentGradientEnd} />
        </linearGradient>
    </svg>
);

export const clearAllRedirect = (cx: { adminConfig?: AdminConfig }): void => {
    const returnUrl = cx.adminConfig?.mall?.returnToUrl;
    const isSSO = !!sessionStorage.getItem(`sid`);

    clearLocalStorage();
    window.sessionStorage.clear();
    if (isSSO) {
        try {
            window.close();
        } catch (e) {
            if (returnUrl) {
                window.location.href = returnUrl as string;
            }
        }
    } else if (window.location.pathname !== `/login`) {
        window.location.href = `/login`;
    }
};

export const logger = (...v: unknown[]): void => {
    const isDebug = !!sessionStorage.getItem(`debug`);
    if (window.location.href.includes(`localhost:`) || window.location.href.includes(`-develop`) || isDebug) {
        // eslint-disable-next-line no-console
        console.log(...v);
    }
};

export const isEmpty = (obj: Record<string, unknown>): boolean => !!Object.keys(obj).length;

export const onlyEmptyKeys = (obj: Record<string, unknown>): boolean => {
    const keyList = Object.keys(obj);
    for (let i = 0; i < keyList.length; i += 1) {
        if (obj[keyList[i]]) {
            return false;
        }
    }
    return true;
};

export const BareAxios = ax;
export const Axios = ax.create();

export const formatMoney = (v: number | string, { noDollarSign }: { noDollarSign?: boolean } = {}): string | number => {
    let result = USDollar.format(v as number);
    if (noDollarSign && result) {
        result = result.replace(`$`, ``);
    }
    return result === `$NaN` ? v : result;
};

export const dateFormat = (dateObj: string | number | Date | null | undefined): string => {
    if (!dateObj) {
        return ``;
    }
    let date: Date;
    if ([`string`, `number`].includes(typeof dateObj)) {
        date = new Date(dateObj as string | number);
    } else if (dateObj instanceof Date) {
        date = dateObj;
    } else {
        return ``;
    }
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

export const getNextFifteen = (
    holidays: Date[] | undefined,
    adminConfig: AdminConfig | undefined
): string | undefined => {
    if (!holidays || !adminConfig) return;
    const mode =
        (adminConfig?.mall?.CORE_SETTINGS as Record<string, string> | undefined)?.[`Payout Method`] === `Request`
            ? `request`
            : `forced`;
    const isAHoliday = (d: Date): boolean =>
        holidays.some(nd => {
            const h = new Date(nd);
            return h?.getDay() === d.getDay() && h?.getFullYear() === d.getFullYear() && h?.getDate() === d.getDate();
        });
    const month = [
        `January`,
        `February`,
        `March`,
        `April`,
        `May`,
        `June`,
        `July`,
        `August`,
        `September`,
        `October`,
        `November`,
        `December`,
    ];

    const payoutMonths = mode === `request` ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] : [0, 3, 6, 9]; // jan apr jul oct

    const oneday = 1000 * 60 * 60 * 24;
    let now = new Date();
    now.setDate(15);
    now.setMonth(payoutMonths.find(p => p > now.getMonth()) ?? 0); // find first payment month greater than current month
    if (now.getMonth() < new Date().getMonth()) {
        now.setFullYear(now.getFullYear() + 1);
    }

    while (now.getDay() === 0 || now.getDay() === 6 || isAHoliday(now)) {
        now = new Date(now.getTime() + oneday);
    }

    return `${month[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;
};

export const currencyFormat = (v: string | number): string => parseFloat(v as string).toFixed(2);

export const maskEmail = (e: string | null | undefined): string => {
    const prelength = 5;
    const postlength = 10;
    if (!e || typeof e !== `string`) return ``;
    const em = e.split(`@`);
    if (em.length !== 2) {
        return ``;
    }
    let pre: string;
    let post: string;
    if (em[0].length <= prelength) {
        [pre] = em;
    } else {
        pre = `${em[0].substr(0, 2)}***`;
    }
    if (em[1].length <= postlength) {
        [, post] = em;
    } else {
        post = `${em[1].substr(0, 3)}***${em[1].substr(-4, 4)}`;
    }
    return `${pre}@${post}`;
};

export const getExtensionLink = (context: AppContext): string | undefined =>
    context?.adminConfig?.mall?.bx_install_link as string | undefined;

export const formatPhone = (value = ``): string => {
    if (!value) return value;

    const leadingone = value.substring(0, 1) === `1`;

    let phoneNumber = value.replace(/[^\d]/g, ``).replace(/^1/, ``);

    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength < 7 && phoneNumberLength > 3) {
        phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }

    if (phoneNumberLength > 6) {
        phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
    return leadingone ? `1 ${phoneNumber}` : phoneNumber;
};

export const hideGrecaptcha = (): void => {
    const gcBadge = document.getElementsByClassName(`grecaptcha-badge`)[0] as HTMLElement | undefined;
    if (gcBadge) {
        gcBadge.style.setProperty(`visibility`, `hidden`);
    }
};

export const mountGrecaptcha = (): void => {
    if (!(window as Window & { grecaptcha?: unknown }).grecaptcha) {
        const gcMount = document.createElement(`script`);
        gcMount.src = `https://www.google.com/recaptcha/api.js?render=6LesMmYbAAAAACONR1kp2flqwlel9cK6gJXDvbkx`;
        document.head.appendChild(gcMount);
    }
    const gcBadge = document.getElementsByClassName(`grecaptcha-badge`)[0] as HTMLElement | undefined;
    if (gcBadge) {
        gcBadge.style.setProperty(`visibility`, `visible`);
    }
};

export const uploadImage = (img: Blob): Promise<string> =>
    Axios.get(`${baseApi}signedurl`).then(r => {
        const myHeaders = new Headers({ "Content-Type": `image/*` });
        return fetch(r.data.signedUrl, {
            method: `PUT`,
            headers: myHeaders,
            body: img,
        }).then(() => r.data.viewUrl as string);
    });

export const getViewportWidth = (): number =>
    Math.min(document.documentElement.clientWidth || 0, window.innerWidth || 0);
export const getViewportHeight = (): number =>
    Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);

export const genericError = (e?: {
    response?: { data?: { msg?: string } };
}): { header: string; text: string; text2: string } => ({
    header: `Sorry, something has gone wrong.`,
    text: `Please try again later.`,
    text2: e?.response?.data?.msg ?? ``,
});

export const removeHash = (): void => {
    history.pushState(``, document.title, `${window.location.pathname}${window.location.search}`);
};

export const scrollToTop = (): void => {
    removeHash();
    window.scrollTo(0, 0);
};

export const calculateBoxes = (containerWidth: number, size: Size): number => {
    const boxWidth = logoBoxSizes[size].w;
    const margin = 25;
    return Math.floor((containerWidth + margin) / (boxWidth + margin));
};

export const getSid = (): string | null => sessionStorage.getItem(`sid`) || localStorage.getItem(`sid`);

interface DisplayableCell extends Store {
    dontShow?: boolean;
}

export const getDisplayableCells = (arr: Store[], up: number, size: Size): Store[] => {
    if (!up) {
        return [];
    }
    const start: (DisplayableCell | 0)[][] = [Array(up).fill(0), Array(up).fill(0), Array(up).fill(0)];
    const output: Store[] = [];
    let currentRow = 0;
    let currentElem = 0;
    for (let i = 0; i < arr.length; i += 1) {
        const a: DisplayableCell = { ...arr[i] };
        if (currentElem >= up) {
            currentRow += 1;
            currentElem = 0;
        }
        if (currentRow > 2) {
            break;
        }

        if (start[currentRow][currentElem]) {
            currentElem += 1;
            i -= 1;
            continue;
        }
        if (a.doubleHigh && currentRow < 2 && size !== `small`) {
            start[currentRow + 1][currentElem] = { ...a, dontShow: true };
        } else {
            a.doubleHigh = false;
        }
        start[currentRow][currentElem] = a;
    }

    const lastRow = start[2].length ? 2 : start[1].length ? 1 : 0;
    let lastRowSingle = false;
    // turn off doubleHigh for all cells in last row after a single low cell;
    start[lastRow] = start[lastRow].map(s => {
        if (!s) {
            return 0;
        }
        if (!s.doubleHigh) {
            lastRowSingle = true;
        }
        return { ...s, doubleHigh: lastRowSingle ? false : s.doubleHigh };
    });
    // turn off doubleHigh for all cells in second to last row that are a high dangle situation
    let secondToLastRowSingle = false;
    if (lastRow > 0) {
        start[lastRow - 1] = start[lastRow - 1].map((s, i) => {
            if (!s) {
                return 0;
            }
            if (!s.doubleHigh && !start[lastRow][i]) {
                secondToLastRowSingle = true;
            }
            return { ...s, doubleHigh: secondToLastRowSingle ? false : s.doubleHigh };
        });
    }

    start.forEach(a => output.push(...(a.filter(b => b && !(b as DisplayableCell).dontShow) as Store[])));
    return output;
};
